// src/components/AppLayout.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { ordersAPI } from '../services/api';
import { toast } from './UI';

// Delivery notification sound (simple beep via Web Audio API)
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch (e) { /* silent fail */ }
}

const BOTTOM_NAV = [
  { to: '/', icon: '', label: 'Dashboard', end: true },
  { to: '/orders', icon: '', label: 'Orders' },
  { to: '/riders', icon: '', label: 'Riders' },
  { to: '/map', icon: '', label: 'Map' },
  { to: '/revenue', icon: '', label: 'Revenue' },
];

export default function AppLayout() {
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const deliveredIdsRef = useRef(null);
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar open on mobile
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  useEffect(() => {
    let isMounted = true;

    const checkOrders = async () => {
      try {
        const orders = await ordersAPI.getAll();
        if (!isMounted) return;

        const active = (orders || []).filter(o => !['Delivered', 'Failed'].includes(o.status));
        setActiveOrdersCount(active.length);

        // Track delivered orders and fire notifications for new ones
        const currentDeliveredIds = new Set(
          (orders || []).filter(o => o.status === 'Delivered').map(o => o.id)
        );

        if (deliveredIdsRef.current === null) {
          deliveredIdsRef.current = currentDeliveredIds;
        } else {
          const newlyDelivered = [...currentDeliveredIds].filter(
            id => !deliveredIdsRef.current.has(id)
          );
          if (newlyDelivered.length > 0) {
            const newOrders = (orders || []).filter(o => newlyDelivered.includes(o.id));
            newOrders.forEach(o => {
              playNotificationSound();
              toast.success(`✅ Delivered! Package ${o.package_id} delivered to ${o.receiver_name}`, 8000);
            });
            deliveredIdsRef.current = currentDeliveredIds;
          }
        }
      } catch (e) { /* silent fail */ }
    };

    checkOrders();
    const interval = setInterval(checkOrders, 20000);
    return () => { isMounted = false; clearInterval(interval); };
  }, []);

  return (
    <div className="app-shell">

      {/* Mobile overlay — tap to close sidebar */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar — passes open state and close handler */}
      <Sidebar
        activeOrdersCount={activeOrdersCount}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />

      <div className="main-area">

        {/* Mobile top bar with hamburger */}
        <header className="mobile-topbar">
          <button
            className="hamburger-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
          <RouteTitle />
        </header>

        <div className="page-content">
          <Outlet />
        </div>
      </div>

    </div>
  );
}

function RouteTitle() {
  const location = useLocation();
  const titles = {
    '/': 'Dashboard', '/orders': 'Orders', '/riders': 'Riders',
    '/map': 'Live Map', '/history': 'History',
    '/revenue': 'Revenue', '/admin': 'Admin',
  };
  const path = '/' + location.pathname.split('/')[1];
  return (
    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--gray-900)' }}>
      {titles[path] || 'Dashboard'}
    </span>
  );
}